// src/utils/searchIndex.js

import router from "@/router";

/**
 * 当前上线版本：
 * 1. 支持 test name 搜索
 * 2. 支持 disease name / category / keyword 搜索
 * 3. 支持点击搜索结果跳转对应页面
 * 4. 暂不显示 gene 搜索文案，避免客户误解
 *
 * 后续如果要支持 gene 搜索，只需要补充 geneMap。
 */

const geneMap = {
    // 后续如果客户提供完整 gene list，在这里补。
    // key 必须和路由最终 path 完全一致。
    //
    // 示例：
    // "/test/Hereditary Cancer": ["BRCA1", "BRCA2", "TP53"],
    // "/test/HearingLoss": ["GJB2", "GJB6", "SLC26A4"]
};

const keywordMap = {
    "/home": ["home", "homepage"],

    "/clinical-case-examples": [
        "Clinical Case Examples",
        "Clinical Case Studies",
        "Case Examples",
        "Case Studies"
    ],

    // ============ ORDER ============
    "/order/testing": [
        "How To Order Testing",
        "Order Testing",
        "Testing",
        "Order"
    ],
    "/order/kit": [
        // "Sample Collection Kit Request Form",
        // "Sample Collection Kit",
        // "Kit Request",
        // "Order Kit"
    ],
    "/order/specimen": [
        "Sample and Shipping Requirements",
        "Sample Requirements",
        "Shipping Requirements",
        "Specimen"
    ],
    "/order/billing": [
        "Billing and Insurance",
        "Billing",
        "Insurance",
        "Payment"
    ],
    "/order/policies": [
        "Lab Policies",
        "Policies",
        "Laboratory Policies"
    ],

    // ============ TEST BASIC ============
    "/test/WholeGenomeSequencingProbandOnly": [
        "WGS",
        "Whole Genome Sequencing",
        "Genome Sequencing",
        "Proband Only"
    ],
    "/test/WholeExomeSequencingProbandOnly": [
        "WES",
        "Whole Exome Sequencing",
        "Exome Sequencing",
        "Proband Only"
    ],
    "/test/Repeat Expansion Disorders": [
        "Repeat Expansion Disorders",
        "Repeat Expansion",
        "Expansion Disorders"
    ],
    "/test/Genome-Wide CNV Analysis": [
        "Genome-Wide CNV Analysis",
        "CNV",
        "Copy Number Variant",
        "Copy Number Variation"
    ],
    "/test/gene": [
        "GENE PANELS",
        "Gene Panel",
        "Genetic Testing",
        "Panels"
    ],

    // ============ AUDIOLOGY ============
    "/test/HearingLoss": [
        "Hearing Loss",
        "Hearing Loss Panel",
        "Audiology",
        "Deafness"
    ],

    // ============ CARDIOLOGY ============
    "/test/AortopathyDisorders": [
        "Aortopathy",
        "Connective Tissue",
        "Aortopathy Disorders",
        "Cardiology"
    ],
    "/test/Arrhythmias": [
        "Arrhythmias",
        "Arrhythmia",
        "Heart Rhythm",
        "Cardiology"
    ],
    "/test/Cardiomyopathy": [
        "Cardiomyopathy",
        "Cardiomyopathy Panel",
        "Heart Disease",
        "Cardiology"
    ],
    "/test/Congenital Heart Defect": [
        "Congenital Heart Defect",
        "Heart Defect",
        "Cardiology"
    ],
    "/test/Familial Hypercholesterolemia": [
        "Familial Hypercholesterolemia",
        "Hypercholesterolemia",
        "Cholesterol",
        "Cardiology"
    ],
    "/test/Noonan Syndrome": [
        "Noonan Syndrome",
        "RASopathy",
        "Cardiology"
    ],
    "/test/Hypertrophic Cardiomyopathy": [
        "Hypertrophic Cardiomyopathy",
        "HCM",
        "Cardiology"
    ],
    "/test/Dilated Cardiomyopathy": [
        "Dilated Cardiomyopathy",
        "DCM",
        "Cardiology"
    ],
    "/test/Arrhythmogenic Right Ventricular/Dysplasia": [
        "Arrhythmogenic Right Ventricular",
        "Dysplasia",
        "ARVC",
        "ARVD",
        "Cardiology"
    ],
    "/test/Left Ventricular Noncompaction": [
        "Left Ventricular Noncompaction",
        "LVNC",
        "Cardiology"
    ],
    "/test/Noonan Syndrome And Other RASopathies": [
        "Noonan Syndrome",
        "RASopathies",
        "RASopathy",
        "Cardiology"
    ],
    "/test/Brugada Syndrome": [
        "Brugada Syndrome",
        "Brugada",
        "Cardiology"
    ],
    "/test/Catecholaminergic Polymorphic Ventricular Tachycardia": [
        "Catecholaminergic Polymorphic Ventricular Tachycardia",
        "CPVT",
        "Ventricular Tachycardia",
        "Cardiology"
    ],
    "/test/Long QT Syndrome": [
        "Long QT Syndrome",
        "LQTS",
        "QT",
        "Cardiology",
        "Arrhythmia"
    ],
    "/test/Short QT Syndrome": [
        "Short QT Syndrome",
        "SQTS",
        "QT",
        "Cardiology",
        "Arrhythmia"
    ],

    // ============ CONNECTIVE TISSUE ============
    "/test/Aortopathy and Connective Tissue Disorders": [
        "Aortopathy",
        "Connective Tissue Disorders",
        "Connective Tissue",
        "Skin"
    ],
    "/test/Connective Tissue Disorders": [
        "Connective Tissue Disorders",
        "Connective Tissue",
        "Skin"
    ],

    // ============ ENDOCRINOLOGY ============
    "/test/Comprehensive Diabetes and Obesity": [
        "Comprehensive Diabetes and Obesity",
        "Diabetes",
        "Obesity",
        "Endocrinology"
    ],
    "/test/Maturity-Onset Diabetes of the Young": [
        "Maturity-Onset Diabetes of the Young",
        "MODY",
        "Diabetes",
        "Endocrinology"
    ],
    "/test/Neonatal Diabetes Mellitus": [
        "Neonatal Diabetes Mellitus",
        "Neonatal Diabetes",
        "Diabetes Mellitus",
        "Diabetes",
        "Endocrinology"
    ],
    "/test/Inherited Obesity": [
        "Inherited Obesity",
        "Obesity",
        "Endocrinology"
    ],

    // ============ GASTROENTEROLOGY ============
    "/test/Hereditary Liver Disease": [
        "Hereditary Liver Disease",
        "Liver Disease",
        "Gastroenterology",
        "Liver"
    ],

    // ============ HEALTHY SCREENING ============
    "/test/Healthy Screening Basic": [
        "Healthy Screening Basic",
        "Healthy Screening",
        "Basic Panel",
        "Screening"
    ],
    "/test/Healthy Screening Expanded": [
        "Healthy Screening Expanded",
        "Healthy Screening",
        "Expanded Panel",
        "Screening"
    ],

    // ============ HEMATOLOGY ============
    "/test/Anemia": [
        "Anemia",
        "Hematology"
    ],
    "/test/Bleeding Disorders": [
        "Bleeding Disorders",
        "Bleeding",
        "Hematology"
    ],
    "/test/Bone Marrow Failure Disorders": [
        "Bone Marrow Failure Disorders",
        "Bone Marrow Failure",
        "Hematology"
    ],
    "/test/Fanconi Anemia": [
        "Fanconi Anemia",
        "Anemia",
        "Hematology"
    ],
    "/test/F8 Inversion": [
        "F8 Inversions",
        "F8",
        "Hemophilia",
        "Hematology"
    ],
    "/test/Platelet Disease": [
        "Platelet Disease",
        "Platelet",
        "Hematology"
    ],

    // ============ IMMUNOLOGY ============
    "/test/Autoimmunity and Autoinflammatory Disease": [
        "Autoimmunity",
        "Autoinflammatory Disease",
        "Autoimmune",
        "Immunology"
    ],
    "/test/Primary Immunodeficiencies and Cytopenia": [
        "Primary Immunodeficiencies",
        "Cytopenia",
        "Immunodeficiency",
        "Immunology"
    ],

    // ============ METABOLIC ============
    "/test/Lysosomal Storage Disease": [
        "Lysosomal Storage Disease",
        "Lysosomal Storage Disorders",
        "Metabolic"
    ],

    // ============ NEPHROLOGY ============
    "/test/Comprehensive Renal Disease": [
        "Comprehensive Renal Disease",
        "Renal Disease",
        "Kidney Disease",
        "Nephrology"
    ],

    // ============ NEUROLOGY ============
    "/test/Muscular Dystrophy Panel": [
        "Muscular Dystrophy",
        "Muscular Dystrophy Panel",
        "Neurology"
    ],
    "/test/Myopathy Panel": [
        "Myopathy",
        "Myopathy Panel",
        "Neurology"
    ],
    "/test/Parkinson’s Disease, Alzheimer’s Disease, and Dementia": [
        "Parkinson",
        "Parkinson’s Disease",
        "Alzheimer",
        "Alzheimer’s Disease",
        "Dementia",
        "Neurology"
    ],

    // ============ ONCOLOGY ============
    "/test/Hereditary Cancer": [
        "Hereditary Cancer",
        "Cancer",
        "Oncology",
        "Tumor"
    ],

    // ============ OPHTHALMOLOGY ============
    "/test/Comprehensive Eye Disorders": [
        "Comprehensive Eye Disorders",
        "Eye Disorders",
        "Ophthalmology",
        "Eye"
    ],

    // ============ PHARMACOGENOMIC ============
    "/test/Pharmacogenomic Panel": [
        "Pharmacogenomic",
        "Pharmacogenomics",
        "PGx",
        "Medication",
        "Drug Response"
    ],

    // ============ PULMONOLOGY ============
    "/test/Comprehensive Ciliopathy": [
        "Comprehensive Ciliopathy",
        "Ciliopathy",
        "Pulmonology"
    ],
    "/test/Heterotaxy": [
        "Heterotaxy",
        "Pulmonology"
    ],
    "/test/Interstitial Lung Disease": [
        "Interstitial Lung Disease",
        "Pulmonary Fibrosis",
        "Surfactant Dysfunction",
        "Pulmonology",
        "Lung Disease"
    ],

    // ============ SKELETAL ============
    "/test/Osteogenesis Imperfecta": [
        "Osteogenesis Imperfecta",
        "Skeletal",
        "Dental",
        "Bone"
    ],
    "/test/Skeletal Dysplasia": [
        "Skeletal Dysplasia",
        "Skeletal",
        "Dental",
        "Bone"
    ],

    // ============ OTHER ============
    "/test/Mitochondrial Genome Sequencing": [
        "Mitochondrial Genome Sequencing",
        "Mitochondrial",
        "Mitochondria"
    ],

    // ============ SERVICES ============
    "/Services/AilisNGS": [
        "AilisNGS",
        "NGS",
        "Services"
    ],
    "/Services/BioPharmaPartnerships": [
        "BioPharma Partnerships",
        "BioPharma",
        "Partnerships",
        "Services"
    ],

    // ============ PRODUCTS ============
    "/products": [
        "Products",
        "Services",
        "Products and Services"
    ],
    "/products/Brochures": [
        "Brochures",
        "Products Brochures"
    ],
    "/products/VariantInterpretationService": [
        "Variant Interpretation Service",
        "Variant Interpretation",
        "Products"
    ],
    "/products/RequestGeneticistConsultation": [
        "Request a Geneticist Consultation",
        "Geneticist Consultation",
        "Consultation"
    ],
    "/products/UnderstandingResults": [
        "Understanding Results",
        "Results"
    ],

    // ============ ABOUT ============
    "/about": [
        "About AiLife",
        "About"
    ],
    "/about/people": [
        "Our People",
        "People",
        "Team"
    ],
    "/about/mission": [
        "Our Mission",
        "Mission"
    ],
    "/about/licenses": [
        "Our Licenses",
        "Licenses"
    ],
    "/about/publications": [
        "Our Publications",
        "Publications"
    ],
    "/about/cantact": [
        "Contact Us",
        "Contact"
    ],
    "/about/privacy": [
        "Privacy",
        "Privacy Policy"
    ],

    // ============ MEDIA ============
    "/media": [
        "News",
        "Blogs",
        "Media"
    ],
    "/media/news1": [
        "ASHG 2019 MEETING, HOUSTON",
        "ASHG",
        "Meeting",
        "Houston"
    ],
    "/media/news3": [
        "AiLife Diagnostics Achieves HITRUST Certification",
        "HITRUST",
        "Certification"
    ],
    "/media/news2": [
        "AILIFE DIAGNOSTICS AND BGI AMERICAS PARTNER TO PROVIDE CLINICAL WGS SOLUTION",
        "BGI",
        "WGS",
        "Clinical WGS Solution"
    ],
    "/media/blog1": [
        "Mappability in Calling CNVs in Exomes",
        "Mappability",
        "CNV",
        "Exomes"
    ],
    "/media/blog2": [
        "Public Perceptions of Genomic Data Sharing",
        "Genomic Data Sharing",
        "Data Sharing"
    ],
    "/media/blog3": [
        "Types of laboratory testing for COVID-19",
        "COVID-19",
        "Laboratory Testing"
    ],

    // ============ TOOLS ============
    "/tools": [
        "Tools"
    ],
    "/tools/AutoHPO": [
        "AutoHPO",
        "HPO",
        "Tools"
    ],
    "/tools/DiseaseRK": [
        "DiseaseRK",
        "Disease",
        "Tools"
    ],

    // ============ JOIN ============
    "/join": [
        "We Are Hiring",
        "Hiring",
        "Careers",
        "Join"
    ]
};

function normalizePath(path) {
    let value = String(path || "");

    value = value.replace(/\/+/g, "/");

    if (value.length > 1 && value.endsWith("/")) {
        value = value.slice(0, -1);
    }

    return value || "/";
}

function joinPath(parentPath, childPath) {
    const parent = String(parentPath || "");
    const child = String(childPath || "");

    if (!child) {
        return normalizePath(parent || "/");
    }

    if (child.startsWith("/")) {
        return normalizePath(child);
    }

    if (!parent || parent === "/") {
        return normalizePath(`/${child}`);
    }

    return normalizePath(`${parent.replace(/\/$/, "")}/${child}`);
}

function getTitle(route) {
    if (route && route.meta && route.meta.title) {
        return route.meta.title;
    }

    if (route && route.name) {
        return route.name;
    }

    return "";
}

function getCategory(path) {
    if (path.startsWith("/test")) return "Tests";
    if (path.startsWith("/order")) return "Order";
    if (path.startsWith("/products")) return "Products & Services";
    if (path.startsWith("/about")) return "About";
    if (path.startsWith("/media")) return "News & Blogs";
    if (path.startsWith("/tools")) return "Tools";
    if (path.startsWith("/Services")) return "Services";
    if (path.startsWith("/clinical-case-examples")) return "Clinical Case Examples";
    if (path.startsWith("/join")) return "Careers";
    if (path.startsWith("/home")) return "Home";

    return "Site";
}

function isParentRoute(route) {
    return Array.isArray(route.children) && route.children.length > 0;
}

function shouldIncludeRoute(route, fullPath) {
    if (!route) return false;
    if (route.path === "*") return false;
    if (!getTitle(route)) return false;

    // 不让 Sample Collection Kit Request Form 出现在网站搜索结果中
    // 注意：这里只是从搜索中移除，不删除页面、不删除路由
    if (fullPath === "/order/kit") return false;
    
    // 动态路由不要作为具体搜索结果，例如 /test/category/:type
    if (fullPath.includes(":")) return false;

    // 父级容器页面不直接作为搜索结果，避免跳转空容器
    if (isParentRoute(route)) return false;

    // redirect 不是最终页面
    if (route.redirect) return false;

    return true;
}

function uniqueArray(arr) {
    return Array.from(
        new Set(
            (arr || [])
                .filter(item => item !== undefined && item !== null)
                .map(item => String(item).trim())
                .filter(Boolean)
        )
    );
}

function flattenRoutes(routes, parentPath = "") {
    let list = [];

    (routes || []).forEach(route => {
        const fullPath = joinPath(parentPath, route.path);
        const title = getTitle(route);

        if (shouldIncludeRoute(route, fullPath)) {
            const category = getCategory(fullPath);
            const genes = uniqueArray(geneMap[fullPath] || []);
            const extraKeywords = uniqueArray(keywordMap[fullPath] || []);

            list.push({
                title,
                path: fullPath,
                category,
                keywords: uniqueArray([
                    title,
                    fullPath,
                    category,
                    ...extraKeywords
                ]),
                genes
            });
        }

        if (route.children && route.children.length) {
            list = list.concat(flattenRoutes(route.children, fullPath));
        }
    });

    return list;
}

function dedupeSearchIndex(list) {
    const map = new Map();

    list.forEach(item => {
        const key = `${item.path}__${item.title}`;

        if (!map.has(key)) {
            map.set(key, item);
        }
    });

    return Array.from(map.values());
}

const searchIndex = dedupeSearchIndex(flattenRoutes(router.options.routes));

export const hasGeneSearchData = searchIndex.some(item => {
    return Array.isArray(item.genes) && item.genes.length > 0;
});

export default searchIndex;


// WEBPACK FOOTER //
// ./src/utils/searchIndex.js